// Reconciliation API Service
// Provides API methods for the reconciliation workbench

import { apiClient } from './apiClient';

const RECONCILIATION_BASE = '/reconciliation';

export const reconciliationService = {
    // Patient Account Balance
    getPatientAccountBalance: async (patientId, filters = {}) => {
        const params = new URLSearchParams();
        if (filters.claimId) params.append('claimId', filters.claimId);
        const response = await apiClient.get(`${RECONCILIATION_BASE}/patient-account-balance/${patientId}?${params.toString()}`);
        return response.data;
    },

    // Session Management
    createSession: async (sessionData) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions`, sessionData);
        return response.data;
    },

    getSession: async (sessionId) => {
        const response = await apiClient.get(`${RECONCILIATION_BASE}/sessions/${sessionId}`);
        return response.data;
    },

    getClaimPaymentDetails: async (claimId) => {
        const response = await apiClient.get(`${RECONCILIATION_BASE}/claim-payment-details/${claimId}`);
        return response.data;
    },

    getSessionByMedication: async (medicationId, rxNumber) => {
        const params = rxNumber ? `?rxNumber=${encodeURIComponent(rxNumber)}` : '';
        const response = await apiClient.get(`${RECONCILIATION_BASE}/sessions-by-medication/${medicationId}${params}`);
        return response.data;
    },

    listSessions: async (filters = {}) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });
        const response = await apiClient.get(`${RECONCILIATION_BASE}/sessions?${params.toString()}`);
        return response.data;
    },

    updateSession: async (sessionId, updates) => {
        const response = await apiClient.patch(`${RECONCILIATION_BASE}/sessions/${sessionId}`, updates);
        return response.data;
    },

    appendAllocations: async (sessionId, allocations) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions/${sessionId}/append-allocations`, { allocations });
        return response.data;
    },

    applySession: async (sessionId, allocations, interestData = {}) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions/${sessionId}/apply`, {
            allocations,
            ...interestData
        });
        return response.data;
    },

    importEobSuggestions: async (sessionId, payload) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions/${sessionId}/import-eob-suggestions`, payload);
        return response.data;
    },

    voidSession: async (sessionId, reason) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions/${sessionId}/void`, { reason });
        return response.data;
    },

    reopenSession: async (sessionId, reason) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions/${sessionId}/reopen`, { reason });
        return response.data;
    },

    deleteSession: async (sessionId) => {
        const response = await apiClient.delete(`${RECONCILIATION_BASE}/sessions/${sessionId}`);
        return response.data;
    },

    removeAllocations: async (sessionId, targetAllocations, reason) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/sessions/${sessionId}/remove-allocations`, {
            targetAllocations,
            reason
        });
        return response.data;
    },

    // Calculate distribution (preview)
    calculateDistribution: async (data) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/calculate`, data);
        return response.data;
    },

    // Search
    searchMedications: async (criteria = {}) => {
        const params = new URLSearchParams();
        Object.entries(criteria).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value)) {
                    params.append(key, value.join(','));
                } else {
                    params.append(key, value);
                }
            }
        });
        const response = await apiClient.get(`${RECONCILIATION_BASE}/search?${params.toString()}`);
        return response.data;
    },

    // Grid filter persistence
    updateFilters: async (sessionId, filters) => {
        const response = await apiClient.put(`${RECONCILIATION_BASE}/sessions/${sessionId}/filters`, filters);
        return response.data;
    },

    // Suspense / Unallocated Funds
    getSuspenseByPayer: async (payerName) => {
        const response = await apiClient.get(`${RECONCILIATION_BASE}/suspense/${encodeURIComponent(payerName)}`);
        return response.data;
    },

    // Check for incomplete session conflicts
    checkConflicts: async (medicationIds, rxNumbers, checkNumber) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/check-conflicts`, {
            medicationIds,
            rxNumbers,
            checkNumber
        });
        return response.data;
    },

    // Check if check number already exists in any session
    checkCheckNumber: async (checkNumber) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/check-checknumber`, { checkNumber });
        return response.data;
    },

    // Agentic EOB Extraction (Parallel)
    analyzeAgenticEob: async (documentId, layoutId, expectedFinancialControls) => {
        const response = await apiClient.post(`${RECONCILIATION_BASE}/agent/analyze`, {
            documentId,
            layoutId,
            expectedFinancialControls,
        });
        return response.data;
    },

    // Get completed session allocation count (for subscription/limit tracking)
    getCompletedAllocationCount: async () => {
        const response = await apiClient.get(`${RECONCILIATION_BASE}/sessions/completed-allocations/count`);
        return response.data;
    }
};

export default reconciliationService;
